import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getMe, updateTheme, type Me } from '@/api/me';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'sportadmin:theme';

// Shared with the Zitadel login page (zitadel/login-runtime/overrides.js).
// localStorage can't cross the go./auth. subdomain boundary, so this cookie —
// scoped to the parent domain in prod — is the only first-paint sync channel
// between the SPA and the login screen. Account preference still wins post-auth
// (the server-reconcile effect below overwrites first paint); the cookie just
// keeps the login→app transition flicker-free and seeds a fresh browser.
const COOKIE_KEY = 'sa_theme';

// readSystemTheme picks the initial theme from the user's OS preference.
// Used when there's no stored value and we haven't yet heard from the server.
function readSystemTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readThemeCookie(): Theme | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const p = part.trim();
    if (p.startsWith(`${COOKIE_KEY}=`)) {
      const v = p.slice(COOKIE_KEY.length + 1);
      if (v === 'light' || v === 'dark') return v;
    }
  }
  return null;
}

function writeThemeCookie(theme: Theme) {
  if (typeof document === 'undefined') return;
  let attrs = `${COOKIE_KEY}=${theme};path=/;max-age=31536000;samesite=lax`;
  // Parent-domain cookie only on the real domain; host-only locally (the SPA
  // and Zitadel run on different localhost ports, so they can't share it in
  // dev — expected gap).
  if (window.location.hostname.endsWith('sportadmin.io')) {
    attrs += ';domain=.sportadmin.io';
  }
  document.cookie = attrs;
}

// Precedence: shared cookie (most recent cross-surface choice, incl. the login
// screen) → localStorage → OS preference. The server preference reconciles on
// top once authenticated.
function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const cookie = readThemeCookie();
  if (cookie) return cookie;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* localStorage blocked */
  }
  return readSystemTheme();
}

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  // Mirror into the shared cookie so the login screen tracks the account's
  // theme on its next render.
  writeThemeCookie(theme);
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* localStorage blocked — DOM still reflects the choice for this session. */
  }
}

// useTheme owns the resolved theme for the SPA. The initial value comes from
// the pre-mount script in index.html (mirrored here from localStorage); once
// the user is authenticated we reconcile against the server value, and any
// toggle persists to both localStorage and the server.
export function useTheme() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();

  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  // Pull the server-side preference once authenticated. If it's set and
  // differs from what we have locally, the server wins — this is what makes
  // the toggle sync across devices.
  const meQuery = useQuery<Me>({
    queryKey: ['me'],
    queryFn: () => getMe(token),
    enabled: !!token,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    const serverTheme = meQuery.data?.themePreference;
    if (serverTheme && serverTheme !== theme) {
      setThemeState(serverTheme);
      applyTheme(serverTheme);
    }
  }, [meQuery.data?.themePreference, theme]);

  // Keep the DOM and localStorage in sync whenever React state changes —
  // covers the toggle path and any future programmatic setTheme.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback(
    (next: Theme) => {
      setThemeState(next);
      applyTheme(next);
      queryClient.setQueryData<Me | undefined>(['me'], (prev) =>
        prev ? { ...prev, themePreference: next } : prev,
      );
      if (token) {
        // Fire-and-forget: the local change has already taken effect, and a
        // failed write just means the next device load won't pick it up.
        updateTheme(token, next).catch(() => {
          /* swallowed — UI already reflects the choice locally */
        });
      }
    },
    [queryClient, token],
  );

  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return { theme, toggle, setTheme };
}
